import {
    faChevronLeft,
    faChevronRight,
    faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import useProjectPermission from "../../../hooks/useProjectPermission";
import {
    useGetLVByIdQuery,
    useUpdatePositionUnitPriceMutation,
} from "../../../store/services/lvService";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { showToast } from "../../../utils/method";
import "./info-sections.scss";
import LvDetailsPanel from "./sections/LvDetailsPanel";
import ServiceSpecificationsTable from "./sections/ServiceSpecificationsTable";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

const ViewLVPage = () => {
    const { t } = useTranslation();
    const { projectId, lvId } = useParams();
    const navigate = useNavigate();

    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

    const projectDetails = useSelector(selectProjectDetails);

    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canEditLV = !permissionLoading && can(PROJECT_AREA.LV, PROJECT_ACTION.EDIT);

    const {
        data: lvData,
        isLoading: loading,
        refetch: refetchLV,
    } = useGetLVByIdQuery({ projectId, lvId }, { skip: !projectId || !lvId });
    const [updatePositionUnitPrice] = useUpdatePositionUnitPriceMutation();

    const handlePriceUpdate = async (positionId, newPrice) => {
        try {
            await updatePositionUnitPrice({
                projectId,
                lvId,
                positionId,
                unitPrice: newPrice,
            }).unwrap();
            showToast(t("lv.price-updated-success"), { type: "success" });
            await refetchLV();
        } catch (error) {
            console.error("Error updating price:", error);
            showToast(error?.message || t("lv.error-updating-price"), { type: "error" });
            throw error; // Re-throw to let the table component handle it
        }
    };

    const FILTER_ITEMS = [
        {
            type: "backButton",
            key: "back",
            label: t("common.back"),
        },
    ];

    const onFilterAction = (index, key) => {
        if (key === "back") {
            navigate(`/projects/${projectId}/lv`);
        }
    };

    const { gaebData, lvPositions, isNachtraege, name, key } = lvData || {
        gaebData: {},
        lvPositions: [],
        isNachtraege: false,
        name: "",
        key: "",
    };

    const { projectInformation: projectInfo, serviceSpecifications } =
        gaebData || { projectInformation: {}, serviceSpecifications: [] };
    const buyerInfo = projectInfo?.buyer || {};

    let lvName = "";
    if (!isNachtraege) {
        lvName = name || "";
    }
    lvName = [key, name].filter(Boolean).join(" - ") || "";

    if (loading) {
        return (
            <div className="view-lv-page">
                <ProjectHeader
                    projectName={projectDetails.name}
                    tabTitle={`${t("lv.lv-details")} | ${lvName || ""}`}
                />
                <div className="page-container">
                    <div className="d-flex justify-content-center align-items-center vlp-loading">
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t("common.loading")}</span>
                            </div>
                            <p className="mt-3">{t("common.loading")}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="view-lv-page">
            <ProjectHeader
                projectName={projectDetails.name}
                tabTitle={`${t("lv.lv-details")} | ${lvName || ""}`}
            />
            <div className="page-container">
                <FilterBar items={FILTER_ITEMS} onFilterAction={onFilterAction} />

                <div className="view-lv-content">
                    <div className="view-lv-twocol-layout">
                        {/* Left details panel */}
                        <div
                            className={`view-lv-left-panel${leftPanelCollapsed ? " collapsed" : ""}`}
                        >
                            <div
                                className="panel-slim-tab"
                                onClick={() => setLeftPanelCollapsed(false)}
                                title={t("common.expand")}
                            >
                                <FontAwesomeIcon
                                    icon={faFileAlt}
                                    className="panel-slim-tab-icon"
                                />
                                <span className="panel-slim-tab-label">
                                    {t("aufmass.basic-information")}
                                </span>
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className="panel-slim-tab-chevron"
                                />
                            </div>

                            <div className="view-lv-left-panel-header">
                                <div className="panel-header-content">
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    {t("aufmass.basic-information")}
                                </div>
                                <button
                                    className="panel-collapse-btn"
                                    onClick={() => setLeftPanelCollapsed(true)}
                                    title={t("common.collapse")}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                            </div>

                            <div className="view-lv-left-body">
                                <LvDetailsPanel
                                    projectInfo={projectInfo}
                                    buyerInfo={buyerInfo}
                                    serviceSpecifications={serviceSpecifications}
                                />
                            </div>
                        </div>

                        {/* Right work area */}
                        <div className="view-lv-right-area">
                            <div className="view-lv-table-wrapper">
                                <ServiceSpecificationsTable
                                    serviceSpecifications={serviceSpecifications}
                                    lvPositions={lvPositions}
                                    isNachtraege={isNachtraege}
                                    lvId={lvId}
                                    onPriceUpdate={handlePriceUpdate}
                                    canEditLV={canEditLV}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewLVPage;
